import * as React from 'react';
import Button from '@mui/material/Button';
import {Avatar, Alert, Box, CircularProgress, Grid, Pagination, Stack, Typography} from '@mui/material';
import {getPaginatedCompetitions, resolveStrapiMediaUrlForDisplay} from '../Api';
import {useNavigate} from 'react-router-dom';
import {DataGrid} from '@mui/x-data-grid/DataGrid/DataGrid';
import {GridColDef} from '@mui/x-data-grid/models/colDef/gridColDef';
import {GridCellParams} from '@mui/x-data-grid/models/params/gridCellParams';
import {DateTimePicker} from '@mui/x-date-pickers/DateTimePicker';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import CompetitionDto from "../models/dtos/CompetitionDto";

export const formatRevenue = (revenue: number) => {
    return revenue.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
    });
}

export default function CompetitionsPage() {
    let defaultLimit = 25;
    let before = new Date();
    let after = new Date();
    before.setDate(before.getDate() + 7);
    after.setDate(after.getDate() - 7);

    const [state, setState] = React.useState({
        before: before,
        after: after,
        loaded: false,
        currentPage: 0,
        limit: defaultLimit,
        totalPages: 0,
        matchState: 1,
        beforeDisplay: before,
        afterDisplay: after,
        searchTerm: undefined as string | undefined,
        competitions: [] as CompetitionDto[],
        loadError: null as string | null,
    });

    const [providerSyncLoading, setProviderSyncLoading] = React.useState(false);

    const navigate = useNavigate();

    const onCompetitionEditClicked = async (competitionId: number) => {
        navigate('/competitions/' + competitionId + '/edit');
    }
    /*
        const getMatchStateText = (state: number) => {
            switch (state) {
                case 1: return 'Future';
                case 2: return 'Live';
                case 3: return 'Finished';
                default: return 'Processing';
                case 5: return 'Cancelled';
            }
        }*/

    const competitionStateLabel = (state?: number) => {
        switch (state) {
            case 1: return 'Pending';
            case 2: return 'Processing';
            case 3: return 'Started';
            case 4: return 'Finished';
            case 5: return 'Canceled';
            default: return state != null ? String(state) : '—';
        }
    }

    const columns: GridColDef[] = [
        {field: 'id', headerName: 'id', width: 128},
        {
            field: 'backgroundUrl',
            headerName: 'Banner',
            width: 100,
            renderCell: (params: GridCellParams) => {
                const src = resolveStrapiMediaUrlForDisplay(params.row.backgroundUrl as string | null)
                return src ? (
                    <Box
                        component="img"
                        src={src}
                        alt=""
                        sx={{ width: 72, height: 40, objectFit: 'cover', borderRadius: 1 }}
                    />
                ) : (
                    <Typography variant="caption" color="text.secondary">—</Typography>
                )
            },
        },
        {
            field: 'sport.photo', headerName: 'Sport', width: 100, renderCell: (params: GridCellParams) =>
                <React.Fragment><Avatar
                    src={params.row.sport?.photo_url}
                    sx={{width: 40, height: 40}}
                    title={params.row.sport?.name}
                /></React.Fragment>
        },
        {
            field: 'name', headerName: 'Name', width: 150, renderCell: (params: GridCellParams) =>
                <React.Fragment>{params.row?.name}</React.Fragment>
        },
        {
            field: 'state', headerName: 'State', width: 110, renderCell: (params: GridCellParams) =>
                <React.Fragment>{competitionStateLabel(params.row.state as number | undefined)}</React.Fragment>
        },
        {
            field: 'date', headerName: 'Date', width: 250, renderCell: (params: GridCellParams) =>
                <React.Fragment>{new Date(params.row.date).toLocaleString()}</React.Fragment>
        },
        {
            field: 'close_date', headerName: 'Close Date', width: 250, renderCell: (params: GridCellParams) =>
                <React.Fragment>{new Date(params.row.closeDate).toLocaleString()}</React.Fragment>
        },
        {
            field: 'enter_fee', headerName: 'Enter Fee', width: 100, renderCell: (params: GridCellParams) =>
                <React.Fragment>{params.row.enterFee} </React.Fragment>
        },
        {
            field: 'prize_pool', headerName: 'Prize Pool', width: 100, renderCell: (params: GridCellParams) =>
                <React.Fragment>{params.row.prizePool} </React.Fragment>
        },
        {
            field: 'jack_pot', headerName: 'Jack Pot', width: 100, renderCell: (params: GridCellParams) =>
                <React.Fragment>{params.row.jackpot} </React.Fragment>
        },
        {
            field: 'actions', headerName: 'Actions', width: 250, renderCell: (params: GridCellParams) =>
                <React.Fragment><Button variant="contained" fullWidth={true} style={{height: '32px'}}
                                        onClick={() => onCompetitionEditClicked(params.row.id)}>Edit</Button></React.Fragment>
        },
    ];


    const fetchData = async (currentPage: number, limit: number, before: Date, after: Date) => {
        setState(prevState => ({...prevState, loaded: false, loadError: null}));
        try {
            const responseComp = await getPaginatedCompetitions(currentPage * limit, limit)
            const competitions = responseComp?.items ?? [];
            const totalItems = responseComp?.totalItems ?? 0;
            const totalPages = Math.ceil(totalItems / limit)
            setState(prevState => ({
                ...prevState,
                before: before,
                after: after,
                competitions: competitions,
                loaded: true,
                totalPages: totalPages,
                currentPage: currentPage,
                loadError: null,
            }));
        } catch (e) {
            const msg = e instanceof Error ? e.message : 'Failed to load competitions'
            setState(prevState => ({
                ...prevState,
                competitions: [],
                loaded: true,
                totalPages: 0,
                loadError: msg,
            }));
        }
    }

    const onPageChanged = async (p: number) => {
        await fetchData(p - 1, state.limit, state.before, state.after);
    }


    React.useEffect(() => {
        fetchData(state.currentPage, state.limit, state.before, state.after);
    }, []);

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div><Stack gap="50px 50px" sx={{mx: 5, my: 5}} justifyContent='center' alignItems='center'>
                <Grid container spacing={2} columns={{xs: 1, sm: 2, md: 4, lg: 6}} alignItems='center'>
                    <Grid item xs={0} sm={1} md={1} lg={2}/>
                    <Box width="100%"/>
                    <Grid item xs={1} sm={1} md={1} lg={1}>
                        <Typography variant="h3">Competitions</Typography>
                    </Grid>

                    <Grid item xs={1} sm={1} md={1} lg={1}>
                        <Button variant="contained" fullWidth={true} style={{height: '54px'}}
                                onClick={() => navigate('/competitions/create')}>Create new</Button>
                    </Grid>
                    <Box width="100%"/>
                </Grid>
                <Pagination count={state.totalPages} size="medium" onChange={(e, p) => onPageChanged(p)}
                            page={state.currentPage + 1}/>
                {state.loadError ? (
                    <Alert severity="error" sx={{ width: '100%' }}>{state.loadError}</Alert>
                ) : null}
                {!state.loaded ?
                    <Grid item xs={2}><CircularProgress size='100px' style={{marginBottom: '32px'}}/></Grid> :
                    <DataGrid sx={{width: '100%'}}
                              rows={state.competitions}
                              columns={columns}
                              initialState={{
                                  pagination: {
                                      paginationModel: {page: 0, pageSize: 25},
                                  },
                              }}
                              pageSizeOptions={[5, 10]}
                              hideFooterPagination

                    />}
            </Stack>
            </div>
        </LocalizationProvider>
    );
}